import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown } from 'lucide-react';

interface DateFilterProps {
    onFilterChange: (startDate: string | null, endDate: string | null) => void;
}

type FilterType = 'today' | 'yesterday' | 'last7' | 'last30' | 'month' | 'year' | 'all' | 'custom';

const FILTER_LABELS: Record<FilterType, string> = {
    today: 'Hoje',
    yesterday: 'Ontem',
    last7: 'Últimos 7 dias',
    last30: 'Últimos 30 dias',
    month: 'Este Mês',
    year: 'Este Ano',
    all: 'Tudo',
    custom: 'Personalizado'
};

function getDateRange(type: FilterType): { start: Date | null; end: Date | null } {
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = new Date();

    switch (type) {
        case 'today':
            start = new Date(now);
            start.setHours(0, 0, 0, 0);
            end = new Date(now);
            end.setHours(23, 59, 59, 999);
            break;
        case 'yesterday':
            start = new Date(now);
            start.setDate(start.getDate() - 1);
            start.setHours(0, 0, 0, 0);
            end = new Date(now);
            end.setDate(end.getDate() - 1);
            end.setHours(23, 59, 59, 999);
            break;
        case 'last7':
            start = new Date(now);
            start.setDate(start.getDate() - 7);
            start.setHours(0, 0, 0, 0);
            break;
        case 'last30':
            start = new Date(now);
            start.setDate(start.getDate() - 30);
            start.setHours(0, 0, 0, 0);
            break;
        case 'month':
            start = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
        case 'year':
            start = new Date(now.getFullYear(), 0, 1);
            break;
        case 'all':
            start = null;
            end = null;
            break;
        default:
            break;
    }
    return { start, end };
}

export const DateFilter: React.FC<DateFilterProps> = ({ onFilterChange }) => {
    const [activeFilter, setActiveFilter] = useState<FilterType>('month');
    const [isCustomOpen, setIsCustomOpen] = useState(false);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const applyFilter = (type: FilterType) => {
        setActiveFilter(type);
        setIsCustomOpen(type === 'custom');

        if (type === 'custom') return;

        const { start, end } = getDateRange(type);
        onFilterChange(start ? start.toISOString() : null, end ? end.toISOString() : null);
    };

    const applyCustomFilter = () => {
        if (customStart && customEnd) {
            const start = new Date(customStart);
            start.setHours(0, 0, 0, 0);
            const end = new Date(customEnd);
            end.setHours(23, 59, 59, 999);
            onFilterChange(start.toISOString(), end.toISOString());
        }
    };

    // ─── Mobile: Select dropdown ───
    if (isMobile) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px', width: '100%' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'var(--glass-bg)',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: 'var(--glass-border)'
                }}>
                    <Filter size={16} color="#888" />
                    <div style={{ position: 'relative', flex: 1 }}>
                        <select
                            value={activeFilter}
                            onChange={(e) => applyFilter(e.target.value as FilterType)}
                            style={{
                                width: '100%',
                                appearance: 'none',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '10px',
                                color: '#fff',
                                padding: '10px 36px 10px 14px',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                outline: 'none',
                            }}
                        >
                            {(Object.keys(FILTER_LABELS) as FilterType[]).map(key => (
                                <option key={key} value={key} style={{ background: '#1a1a1a', color: '#fff' }}>
                                    {FILTER_LABELS[key]}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={14} color="#888" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                    </div>
                </div>

                {isCustomOpen && (
                    <CustomDateInputs
                        customStart={customStart}
                        customEnd={customEnd}
                        setCustomStart={setCustomStart}
                        setCustomEnd={setCustomEnd}
                        onApply={applyCustomFilter}
                    />
                )}
            </div>
        );
    }

    // ─── Desktop: Pill buttons ───
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px', width: '100%' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                background: 'var(--glass-bg)',
                padding: '8px',
                borderRadius: '12px',
                border: 'var(--glass-border)'
            }}>
                <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#888' }}>
                    <Filter size={16} />
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>Período:</span>
                </div>

                {(Object.keys(FILTER_LABELS) as FilterType[]).map(key => (
                    <FilterButton key={key} active={activeFilter === key} onClick={() => applyFilter(key)}>
                        {FILTER_LABELS[key]}
                        {key === 'custom' && <ChevronDown size={14} style={{ marginLeft: '4px' }} />}
                    </FilterButton>
                ))}
            </div>

            {isCustomOpen && (
                <CustomDateInputs
                    customStart={customStart}
                    customEnd={customEnd}
                    setCustomStart={setCustomStart}
                    setCustomEnd={setCustomEnd}
                    onApply={applyCustomFilter}
                />
            )}
        </div>
    );
};

// ─── Sub-components ───

const CustomDateInputs: React.FC<{
    customStart: string;
    customEnd: string;
    setCustomStart: (v: string) => void;
    setCustomEnd: (v: string) => void;
    onApply: () => void;
}> = ({ customStart, customEnd, setCustomStart, setCustomEnd, onApply }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'rgba(0,0,0,0.2)',
        padding: '12px',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.05)',
        animation: 'fadeIn 0.2s ease',
        flexWrap: 'wrap'
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#888' }}>De:</span>
            <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={dateInputStyle}
            />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#888' }}>Até:</span>
            <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={dateInputStyle}
            />
        </div>
        <button
            onClick={onApply}
            disabled={!customStart || !customEnd}
            style={{
                background: (!customStart || !customEnd) ? 'rgba(255,255,255,0.1)' : 'var(--primary)',
                color: '#fff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: (!customStart || !customEnd) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: (!customStart || !customEnd) ? 0.5 : 1
            }}
        >
            Aplicar
        </button>
    </div>
);

const dateInputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    colorScheme: 'dark' as any
};

const FilterButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
    <button
        onClick={onClick}
        style={{
            background: active ? 'rgba(255, 106, 0, 0.15)' : 'transparent',
            border: `1px solid ${active ? 'var(--primary)' : 'rgba(255,255,255,0.05)'}`,
            color: active ? 'var(--primary)' : '#aaa',
            padding: '6px 16px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: active ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
        }}
        onMouseOver={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
        onMouseOut={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
        {children}
    </button>
);
